import React, { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AlertProvider } from "@/components/AlertContext";
import VersionChecker from "@/components/VersionChecker";

// ============================================
// LAZY LOADING - Pages chargées à la demande
// Réduit le bundle initial de ~60%
// ============================================
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Equipment = lazy(() => import("@/pages/Equipment"));
const MediaLibrary = lazy(() => import("@/pages/MediaLibrary"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const History = lazy(() => import("@/pages/History"));
const Users = lazy(() => import("@/pages/Users"));
const CalendarView = lazy(() => import("@/pages/CalendarView"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Complaints = lazy(() => import("@/pages/Complaints"));
const Rooms = lazy(() => import("@/pages/Rooms"));
const LostAndFound = lazy(() => import("@/pages/LostAndFound"));
const PreventiveMaintenanceNew = lazy(() => import("@/pages/PreventiveMaintenanceNew"));
const EmailLogs = lazy(() => import("@/pages/EmailLogs"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminImport = lazy(() => import("@/pages/AdminImport"));
const ScanRoom = lazy(() => import("@/pages/ScanRoom"));
const ReportProblem = lazy(() => import("@/pages/ReportProblem"));
const HotelManagement = lazy(() => import("@/pages/HotelManagement"));
const HelpManual = lazy(() => import("@/pages/HelpManual"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const Archives = lazy(() => import("@/pages/Archives"));
const LiveDashboard = lazy(() => import("@/pages/LiveDashboard"));
const PublicLiveDashboard = lazy(() => import("@/pages/PublicLiveDashboard"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const ElectricalPanels = lazy(() => import("@/pages/ElectricalPanels"));
const FireDetectors = lazy(() => import("@/pages/FireDetectors"));
const FireDrillExercises = lazy(() => import("@/pages/FireDrillExercises"));
const StockHousekeeping = lazy(() => import("@/pages/StockHousekeeping"));
const StockKitchen = lazy(() => import("@/pages/StockKitchen"));
const StockMaintenance = lazy(() => import("@/pages/StockMaintenance"));
const StockEvents = lazy(() => import("@/pages/StockEvents"));
const BudgetTracking = lazy(() => import("@/pages/BudgetTracking"));
const OutOfServiceRooms = lazy(() => import("@/pages/OutOfServiceRooms"));
const Prevention = lazy(() => import("@/pages/Prevention"));
const HACCP = lazy(() => import("@/pages/HACCP"));
const ScanHACCP = lazy(() => import("@/pages/ScanHACCP"));
const ScanPatrol = lazy(() => import("@/pages/ScanPatrol"));
const PatrolZones = lazy(() => import("@/pages/PatrolZones"));
const Announcements = lazy(() => import("@/pages/Announcements"));
const DailyReportInteractive = lazy(() => import("@/pages/DailyReportInteractive"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const TrialSignup = lazy(() => import("@/pages/TrialSignup"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const Flushing = lazy(() => import("@/pages/Flushing"));
const DirectorDashboard = lazy(() => import("@/pages/DirectorDashboard"));
const IdeasBox = lazy(() => import("@/pages/IdeasBox"));
const UsefulContacts = lazy(() => import("@/pages/UsefulContacts"));
const MaintenanceJournal = lazy(() => import("@/pages/MaintenanceJournal"));
const CleaningJournal = lazy(() => import("@/pages/CleaningJournal"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// ============================================
// LOADING SKELETON - UX améliorée
// ============================================
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
    <div className="text-center">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-900 rounded-full animate-spin border-t-indigo-600 dark:border-t-indigo-400"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-ping border-t-indigo-400 opacity-20"></div>
      </div>
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 animate-pulse">Chargement...</p>
    </div>
  </div>
);

// ============================================
// API CACHE - Mise en cache intelligente
// ============================================
const apiCache = new Map();
const CACHE_DURATION = 30 * 1000; // 30 seconds for fresh data

// Simple response caching for GET requests
axios.interceptors.response.use((response) => {
  // Only cache GET requests
  if (response.config.method?.toLowerCase() === 'get') {
    const cacheKey = `${response.config.url}`;
    apiCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
  }
  return response;
});

// Clear cache on mutations (POST, PUT, DELETE, PATCH)
axios.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase();
  if (['post', 'put', 'delete', 'patch'].includes(method)) {
    // Clear related cache entries
    const urlPath = config.url?.split('?')[0];
    for (const key of apiCache.keys()) {
      if (key.includes(urlPath?.split('/').slice(0, -1).join('/'))) {
        apiCache.delete(key);
      }
    }
  }
  return config;
});

export const cachedFetch = async (url, options = {}, forceRefresh = false) => {
  const cacheKey = `${url}-${JSON.stringify(options)}`;
  const now = Date.now();
  
  // Vérifier le cache
  if (!forceRefresh && apiCache.has(cacheKey)) {
    const cached = apiCache.get(cacheKey);
    if (now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
  }
  
  // Faire la requête
  const response = await axios.get(url, options);
  
  // Mettre en cache
  apiCache.set(cacheKey, {
    data: response.data,
    timestamp: now
  });
  
  return response.data;
};

export const clearCache = (pattern = null) => {
  if (pattern) {
    for (const key of apiCache.keys()) {
      if (key.includes(pattern)) {
        apiCache.delete(key);
      }
    }
  } else {
    apiCache.clear();
  }
};

// ============================================
// IMAGE OPTIMIZATION HELPER
// ============================================
export const optimizeImageUrl = (url, width = 800, quality = 80) => {
  if (!url) return url;
  // Si c'est une URL externe avec support de transformation
  if (url.includes('cloudinary.com')) {
    return url.replace('/upload/', `/upload/w_${width},q_${quality},f_auto/`);
  }
  return url;
};

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setUser(response.data);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  // Dynamically set the document title based on the current host.
  // - easymaintain.be / www.easymaintain.be → marketing landing → keeps "EasyMaintain" title (SEO)
  // - app.easymaintain.be → app → uses "FixFlow" title
  // Per-page hooks (useSEO) override this for landing/contact/trial-signup.
  useEffect(() => {
    const host = window.location.hostname;
    // Marketing host = root domain (no app subdomain)
    const isMarketingHost = host === 'easymaintain.be' || host === 'www.easymaintain.be';
    const path = window.location.pathname;
    const isPublicPage = ['/landing', '/contact', '/trial-signup'].some(p => path.startsWith(p));

    if (!isMarketingHost && !isPublicPage) {
      document.title = 'FixFlow';
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', 'FixFlow — Maintenance hôtelière professionnelle');
    }
  }, []);

  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    clearCache(); // Clear cache on login
    
    // Request notification permission on first login
    if ('Notification' in window && Notification.permission === 'default') {
      setTimeout(() => {
        Notification.requestPermission();
      }, 2000);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    clearCache(); // Clear cache on logout
    toast.success(t('auth.logoutSuccess'));
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, cachedFetch, clearCache }}>
      <AlertProvider API={API} token={token}>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public route for QR code scanning - no auth required */}
                <Route path="/scan/signaler" element={<ReportProblem />} />
                <Route path="/scan/:roomNumber" element={<ScanRoom />} />
                <Route path="/live/:shareToken" element={<PublicLiveDashboard />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/trial-signup" element={!token ? <TrialSignup /> : <Navigate to="/" />} />
              
              <Route path="/login" element={!token ? <Login /> : <Navigate to="/" />} />
              <Route path="/" element={token ? <Dashboard /> : <Navigate to="/login" />} />
              <Route path="/hotels" element={token ? <HotelManagement /> : <Navigate to="/login" />} />
              <Route path="/equipment" element={token ? <Equipment /> : <Navigate to="/login" />} />
              <Route path="/media-library" element={token ? <MediaLibrary /> : <Navigate to="/login" />} />
              <Route path="/tasks" element={token ? <Tasks /> : <Navigate to="/login" />} />
              <Route path="/calendar" element={token ? <CalendarView /> : <Navigate to="/login" />} />
              <Route path="/inventory" element={token ? <Inventory /> : <Navigate to="/login" />} />
              <Route path="/rooms" element={token ? <Rooms /> : <Navigate to="/login" />} />
              <Route path="/out-of-service" element={token ? <OutOfServiceRooms /> : <Navigate to="/login" />} />
              <Route path="/prevention" element={token ? <Prevention /> : <Navigate to="/login" />} />
              <Route path="/haccp" element={token ? <HACCP /> : <Navigate to="/login" />} />
              <Route path="/flushing" element={token ? <Flushing /> : <Navigate to="/login" />} />
              <Route path="/scan-haccp/:qrCode" element={<ScanHACCP />} />
              <Route path="/patrol/:zoneCode" element={<ScanPatrol />} />
              <Route path="/patrol-zones" element={token ? <PatrolZones /> : <Navigate to="/login" />} />
              <Route path="/report/daily" element={<DailyReportInteractive />} />
              <Route path="/report/daily/:date" element={<DailyReportInteractive />} />
              <Route path="/reports/daily/:date" element={<DailyReportInteractive />} />
              <Route path="/complaints" element={token ? <Complaints /> : <Navigate to="/login" />} />
              <Route path="/history" element={token ? <History /> : <Navigate to="/login" />} />
              <Route path="/expenses" element={token ? <Expenses /> : <Navigate to="/login" />} />
              <Route path="/purchases" element={token ? <Purchases /> : <Navigate to="/login" />} />
              <Route path="/budget-tracking" element={token ? <BudgetTracking /> : <Navigate to="/login" />} />
              <Route path="/archives" element={token ? <Archives /> : <Navigate to="/login" />} />
              <Route path="/analytics" element={<Navigate to="/" replace />} />
              <Route path="/electrical-panels" element={token ? <ElectricalPanels /> : <Navigate to="/login" />} />
              <Route path="/fire-detectors" element={token ? <FireDetectors /> : <Navigate to="/login" />} />
              <Route path="/fire-drills" element={token ? <FireDrillExercises /> : <Navigate to="/login" />} />
              <Route path="/stock-housekeeping" element={token ? <StockHousekeeping /> : <Navigate to="/login" />} />
              <Route path="/stock-kitchen" element={token ? <StockKitchen /> : <Navigate to="/login" />} />
              <Route path="/stock-maintenance" element={token ? <StockMaintenance /> : <Navigate to="/login" />} />
              <Route path="/stock-events" element={token ? <StockEvents /> : <Navigate to="/login" />} />
              <Route path="/lost-and-found" element={token ? <LostAndFound /> : <Navigate to="/login" />} />
              <Route path="/preventive-maintenance" element={token ? <PreventiveMaintenanceNew /> : <Navigate to="/login" />} />
              <Route path="/email-logs" element={token ? <EmailLogs /> : <Navigate to="/login" />} />
              <Route path="/announcements" element={token ? <Announcements /> : <Navigate to="/login" />} />
              <Route path="/admin-settings" element={token ? <AdminSettings /> : <Navigate to="/login" />} />
              <Route path="/live-dashboard" element={token ? <LiveDashboard /> : <Navigate to="/login" />} />
              <Route path="/users" element={token ? <Users /> : <Navigate to="/login" />} />
              <Route path="/admin/import" element={token ? <AdminImport /> : <Navigate to="/login" />} />
              <Route path="/aide" element={token ? <HelpManual /> : <Navigate to="/login" />} />
              <Route path="/direction" element={token ? <DirectorDashboard /> : <Navigate to="/login" />} />
              <Route path="/ideas" element={token ? <IdeasBox /> : <Navigate to="/login" />} />
              <Route path="/useful-contacts" element={token ? <UsefulContacts /> : <Navigate to="/login" />} />
              <Route path="/journal-maintenance" element={token ? <MaintenanceJournal /> : <Navigate to="/login" />} />
              <Route path="/journal-cleaning" element={token ? <CleaningJournal /> : <Navigate to="/login" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <VersionChecker />
      </div>
      </AlertProvider>
    </AuthContext.Provider>
  );
}

export default App;
